// src/components/Quotes/QuoteForm.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "../../api";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import QuoteClientInfo from "./QuoteClientInfo";
import QuoteServices from "./QuoteServices";
import QuoteLogisticsTab from "./QuoteLogisticsTab";
import InventorySelector from "../Inventory/InventorySelector";
import { useAuth } from "../Auth/AuthContext";
import { FaCalendarAlt, FaMapMarkerAlt, FaBoxOpen, FaUserTie, FaCommentDots } from "react-icons/fa";

/* ---------- utils ---------- */
const safeNumber = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const parseMaybeJSON = (val, fallback) => {
  if (val == null) return fallback;
  if (typeof val === "object") return val;
  try {
    return JSON.parse(val);
  } catch {
    return fallback;
  }
};

// Ancienne étiquette qu'on souhaite NE PLUS réinjecter (doublon avec QuoteServices)
const CHARGES_LABEL_LEGACY = "Frais de gestion et exploitation";

// validated_at : datetime SQLite (UTC sans suffixe) → affichage local fr
const fmtDateTime = (val) => {
  if (!val) return "";
  const iso = val.includes("T") ? val : val.replace(" ", "T") + "Z";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("fr-FR", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

// --- date utils ---
const toYMD = (val) => {
  if (!val) return "";
  if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return "";
  }
};

export default function QuoteForm({ onSubmit, initialData }) {
  const { t } = useTranslation();
  const { user } = useAuth();

  /* ---------- State principal ---------- */
  const [formData, setFormData] = useState({
    id: initialData?.id || null,
    date: initialData?.date || "",
    status: initialData?.status || "pending",
    validated_at: initialData?.validated_at || null,
    prestations: [],
    client: initialData?.client || null,
    // champs lus par QuoteClientInfo (toujours des strings)
    client_name: initialData?.client_name || "",
    client_phone: initialData?.client_phone || "",
    client_email: initialData?.client_email || "",
    client_address: initialData?.client_address || "",
    client_city: initialData?.client_city || "",
    client_zip: initialData?.client_zip || "",
    prestation_type: initialData?.prestation_type || "",
    payment_method: initialData?.payment_method || "",
    // Commercial ayant créé le devis en premier (recap only, non modifiable ici).
    commercial_name: initialData?.commercial_name || "",
  });

  // Sous-traitance de la première prestation (ligne "Service déménagement").
  const [providerType, setProviderType] = useState("internal"); // 'internal' | 'subcontractor'
  const [subcontractorId, setSubcontractorId] = useState(null);
  const [subcontractAmount, setSubcontractAmount] = useState(""); // montant HT confié au sous-traitant
  const [subcontractors, setSubcontractors] = useState([]);
  const [discountPercent, setDiscountPercent] = useState(""); // remise commerciale (%)

  useEffect(() => {
    api.get("/subcontractors")
      .then((res) => setSubcontractors((res.data || []).filter((s) => s.active)))
      .catch(() => {}); // best-effort : la fonctionnalité reste utilisable sans la liste
  }, []);

  // Coûts & paramètres (pilotés par QuoteServices)
  const [prestations, setPrestations] = useState([]);
  const [serviceLabel, setServiceLabel] = useState("");
  const [servicePrice, setServicePrice] = useState(0);
  const [distanceKm, setDistanceKm] = useState(0);
  const [wearCoef, setWearCoef] = useState(0);
  const [fuelCost, setFuelCost] = useState(0);
  const [salaries, setSalaries] = useState([{ name: "", salary: 0 }]);
  const [otherCharges, setOtherCharges] = useState([{ label: "", amount: 0 }]);
  const [commercialMargin, setCommercialMargin] = useState(0);

  // Logistique / inventaire
  const [logistics, setLogistics] = useState({});
  const [selectedInventory, setSelectedInventory] = useState([]);

  // Tabs
  const [activeTab, setActiveTab] = useState("info");

  // Volume auto vs manuel
  const [volumeTouched, setVolumeTouched] = useState(false);
  const lastAutoVolumeRef = useRef(null);

  // mapping inventaire: item quelconque -> {id,name,volume,quantity,image_url}
  const mapInventoryItem = useCallback((it) => {
    const id = Number(it.inventory_item_id ?? it.itemId ?? it.id ?? 0) || 0;
    return {
      id,
      name: it.name || `#${id}`,
      volume: safeNumber(it.volume),
      quantity: Math.max(1, safeNumber(it.quantity) || 1),
      image_url: it.image_url || null,
    };
  }, []);

  /* ---------- Pré-remplissage initial (créa / édition) ---------- */
  useEffect(() => {
    const fillFrom = (raw = {}) => {
      setFormData((prev) => ({
        ...prev,
        id: raw.id ?? prev.id ?? null,
        date: toYMD(raw.date || prev.date || ""),
        status: raw.status || prev.status || "pending",
        validated_at: raw.validated_at ?? prev.validated_at ?? null,
        client: raw.client || prev.client || null,
        // Hydrate les champs attendus par QuoteClientInfo
        client_name: raw.client_name ?? raw.clientName ?? raw.client?.name ?? prev.client_name ?? "",
        client_phone: raw.client_phone ?? raw.clientPhone ?? raw.client?.phone ?? prev.client_phone ?? "",
        client_email: raw.client_email ?? raw.clientEmail ?? raw.client?.email ?? prev.client_email ?? "",
        client_address: raw.client_address ?? raw.clientAddress ?? raw.client?.address ?? prev.client_address ?? "",
        client_city: raw.client_city ?? raw.clientCity ?? raw.client?.city ?? prev.client_city ?? "",
        client_zip: raw.client_zip ?? raw.clientZip ?? raw.client?.zip ?? prev.client_zip ?? "",
        prestation_type: raw.prestation_type ?? raw.prestationType ?? prev.prestation_type ?? "",
        payment_method: raw.payment_method ?? prev.payment_method ?? "",
        commercial_name: raw.commercial_name ?? prev.commercial_name ?? "",
        // IMPORTANT : on nettoie les anciennes lignes "charges" (legacy) pour éviter le doublon
        prestations: Array.isArray(raw.prestations)
          ? raw.prestations.filter((p) => (p?.description || "") !== CHARGES_LABEL_LEGACY)
          : [],
      }));

      // Remonter le calcul (si sauvegardé). calc peut être vide/absent (devis legacy).
      const calc = parseMaybeJSON(raw.calculationData, null);
      const hasCalc = calc && typeof calc === "object" && Object.keys(calc).length > 0;

      // Classer les lignes stockées. Nouveau format : tags __fixed auto-descriptifs
      // (SERVICE/LABOR/MGMT). Legacy (sans tags) : fallback matching par libellé i18n.
      const rawPrestasList = (Array.isArray(raw.prestations) ? raw.prestations : [])
        .filter((p) => (p?.description || "") !== CHARGES_LABEL_LEGACY);
      const hasTags = rawPrestasList.some((p) => p && p.__fixed);

      let svcLine, laborLine, mgmtLine, extraLines;
      if (hasTags) {
        svcLine    = rawPrestasList.find((p) => p.__fixed === "SERVICE");
        laborLine  = rawPrestasList.find((p) => p.__fixed === "LABOR");
        mgmtLine   = rawPrestasList.find((p) => p.__fixed === "MGMT");
        extraLines = rawPrestasList.filter((p) => !p.__fixed);
      } else {
        const normStr = (s) =>
          (s || "").toString().toLowerCase().normalize("NFD")
            .replace(/\p{Diacritic}/gu, "").replace(/\s+/g, " ").trim();
        const laborNorm = normStr(t("quote.labor", "Main d'œuvre"));
        const mgmtNorm  = normStr(t("quote.managementFees", "Frais de gestion & exploitation"));
        laborLine = rawPrestasList.find((p) => normStr(p?.description) === laborNorm);
        mgmtLine  = rawPrestasList.find((p) => normStr(p?.description) === mgmtNorm);
        const nonStandard = rawPrestasList.filter((p) => {
          const d = normStr(p?.description || "");
          return d !== laborNorm && d !== mgmtNorm;
        });
        svcLine    = nonStandard[0];        // 1re ligne non-standard = Service déménagement
        extraLines = nonStandard.slice(1);  // le reste = lignes supplémentaires (à préserver)
      }

      const laborTotal = safeNumber(laborLine?.total ?? laborLine?.prix);
      const mgmtTotal  = safeNumber(mgmtLine?.total ?? mgmtLine?.prix);

      setDistanceKm(safeNumber(calc?.distanceKm));
      setWearCoef(safeNumber(calc?.wearCoef));
      setFuelCost(safeNumber(calc?.fuelCost));

      setSalaries(
        Array.isArray(calc?.salaries) && calc.salaries.length
          ? calc.salaries.map((s) => ({ name: s.name || "", salary: safeNumber(s.salary) }))
          : laborTotal > 0
            ? [{ name: "", salary: laborTotal }] // fallback legacy : reconstitue "Main d'œuvre"
            : [{ name: "", salary: 0 }]
      );
      setOtherCharges(
        Array.isArray(calc?.otherCharges) && calc.otherCharges.length
          ? calc.otherCharges.map((c) => ({ label: c.label || "", amount: safeNumber(c.amount) }))
          : [{ label: "", amount: 0 }]
      );
      // Marge : depuis calc si présent, sinon on verse tout le total "Frais de gestion"
      // stocké dans la marge pour que le total recalculé == total affiché en liste.
      setCommercialMargin(hasCalc ? safeNumber(calc?.commercialMargin) : mgmtTotal);

      setLogistics(parseMaybeJSON(raw.logistics, {}));

      // Sous-traitance de la première prestation (ligne "Service déménagement").
      setProviderType(svcLine?.provider_type === "subcontractor" ? "subcontractor" : "internal");
      setSubcontractorId(svcLine?.subcontractor_id ?? null);
      setSubcontractAmount(svcLine?.subcontract_amount ?? "");
      setDiscountPercent(initialData.discount_percent ? String(initialData.discount_percent) : "");

      setServiceLabel(svcLine?.description || "");
      // Devis orphelin : total présent en base mais AUCUNE ligne ni calc stockés
      // (donnée legacy corrompue) → on réinjecte le total HT dans la ligne "Service"
      // pour que l'édition reflète le total de la liste au lieu d'afficher 0.
      const storedTotalHT = safeNumber(raw.totalHT ?? raw.totalFullHT);
      const svcSeed = safeNumber(svcLine?.total ?? svcLine?.prix) || 0;
      const orphanFallback =
        (!rawPrestasList.length && !hasCalc && storedTotalHT > 0) ? storedTotalHT : 0;
      setServicePrice(svcSeed || orphanFallback);

      // Réinjecte les lignes supplémentaires dans l'état piloté par QuoteServices,
      // sinon elles sont écrasées par la reconstruction et perdues à l'édition.
      setPrestations(extraLines);

      setVolumeTouched(false);

      // Inventaire
      const invFull = Array.isArray(raw.inventory) ? raw.inventory : null; // compatibilité backend
      const invItems = parseMaybeJSON(raw.inventoryItems, null);
      const src = invFull ?? invItems ?? [];
      setSelectedInventory(Array.isArray(src) ? src.map(mapInventoryItem) : []);
    };

    // 1) Pré-remplissage rapide si on a déjà initialData
    if (initialData) fillFrom(initialData);

    // 2) Si édition (id), on charge la version complète
    const loadFull = async () => {
      if (!initialData?.id) return;
      const res = await api.get(`/quote/${initialData.id}`);
      fillFrom(res.data || {});
    };
    loadFull();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData?.id]);

  /* ---------- Sync prestations descendantes (depuis QuoteServices) ---------- */
  useEffect(() => {
    // `QuoteServices` pilote la liste, on la remonte dans formData
    setPrestations((prev) => prev); // garde locale (si besoin)
    setFormData((s) => ({ ...s, prestations }));
  }, [prestations]);

  /* ---------- Volume auto basé sur inventaire ---------- */
  const totalInventoryVolume = useMemo(
    () =>
      (selectedInventory || []).reduce(
        (sum, it) => sum + safeNumber(it.volume) * Math.max(1, safeNumber(it.quantity) || 1),
        0
      ),
    [selectedInventory]
  );

  useEffect(() => {
    const auto = Number(totalInventoryVolume.toFixed(2));
    if (!volumeTouched) {
      lastAutoVolumeRef.current = auto;
      setLogistics((prev) => ({ ...prev, volume: auto }));
    }
  }, [totalInventoryVolume, volumeTouched]);

  useEffect(() => {
    const v = safeNumber(logistics?.volume);
    if (lastAutoVolumeRef.current != null && v !== lastAutoVolumeRef.current) {
      setVolumeTouched(true); // l’utilisateur a modifié manuellement
    }
  }, [logistics?.volume]);

  /* ---------- Totaux basés UNIQUEMENT sur formData.prestations ---------- */
  const totalHT = useMemo(
    () =>
      (formData.prestations || []).reduce((sum, p) => {
        const prix = safeNumber(p.prix);
        const qte = p.quantite == null || p.quantite === "" ? 1 : safeNumber(p.quantite); // 0 reste 0
        return sum + prix * qte;
      }, 0),
    [formData.prestations]
  );
  const totalTVA = useMemo(() => totalHT * 0.2, [totalHT]);
  const totalTTC = useMemo(() => totalHT + totalTVA, [totalHT, totalTVA]);

  /* ---------- Handlers ---------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({
      ...s,
      [name]: name === "date" ? toYMD(value) : value,
    }));
  };

const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation minimale — doit couvrir tous les champs marqués requis (*) dans QuoteClientInfo
    const missing = [];
    if (!formData.client_name) missing.push(t("quote.clientName", "Nom du client"));
    if (!formData.date) missing.push(t("quote.date", "Date"));
    if (!formData.client_address) missing.push(t("quote.clientAddress", "Adresse"));
    if (!formData.client_city) missing.push(t("quote.clientCity", "Ville"));
    if (!formData.prestation_type) missing.push(t("quote.prestationType", "Type de prestation"));

    if (missing.length) {
      setActiveTab("info");
      toast.error(
        `⚠️ ${t("quote.missingFields", "Champs requis manquants")} : ${missing.join(", ")}`
      );
      return;
    }

    // Déménagement : code postal d'arrivée (livraison) obligatoire.
    const isMoving = /d[ée]m[ée]nag/i.test(formData.prestation_type || "");
    if (isMoving && !/^\d{5}$/.test(String(logistics?.delivery_zip || "").trim())) {
      setActiveTab("logistics");
      toast.error(t("quote.deliveryZipRequired", "Code postal d'arrivée requis pour un déménagement."));
      return;
    }

    try {
      const formattedDate = toYMD(formData.date);

      // Remise commerciale : le canonique (totalHT/TVA/TTC) est le NET (après remise),
      // les colonnes totalFull* conservent le brut (avant remise) pour l'affichage détaillé.
      const discountPct = Math.min(100, Math.max(0, safeNumber(discountPercent)));
      const factor = 1 - discountPct / 100;
      const round2 = (v) => Number((v * factor).toFixed(2));

      const payload = {
        ...formData,
        status: initialData?.id ? (formData.status || "pending") : "pending",
        date: formattedDate,
        // On conserve les tags __fixed (SERVICE/LABOR/MGMT) : chaque ligne devient
        // auto-descriptive → rechargement fiable sans deviner par libellé i18n.
        prestations: formData.prestations || [],
        totalHT: round2(totalHT),
        totalTVA: round2(totalTVA),
        totalTTC: round2(totalTTC),
        totalFullHT: Number(totalHT.toFixed(2)),
        totalFullTTC: Number(totalTTC.toFixed(2)),
        calculationData: {
          distanceKm: safeNumber(distanceKm),
          wearCoef: safeNumber(wearCoef),
          fuelCost: safeNumber(fuelCost),
          commercialMargin: safeNumber(commercialMargin),
          salaries: salaries.map((s) => ({ name: s.name, salary: safeNumber(s.salary) })),
          otherCharges: otherCharges.map((c) => ({ label: c.label, amount: safeNumber(c.amount) })),
        },
        discount_percent: discountPct,
        logistics: { ...logistics, volume: safeNumber(logistics?.volume) },
        inventoryItems: (selectedInventory || []).map((it) => ({
          inventory_item_id: Number(it.id),
          quantity: Math.max(1, safeNumber(it.quantity)),
        })),
      };

      if (onSubmit) {
        onSubmit(payload);
      }
    } catch (err) {
      console.error(err);
      toast.error(t("quote.saveError"));
    }
  };

  /* ---------- Rendu ---------- */
  return (
    <form className="bg-base-100 rounded-2xl border border-base-200 shadow-sm overflow-hidden" onSubmit={handleSubmit} noValidate>
      {/* Header */}
      <div className="px-6 py-4 border-b border-base-200 bg-base-100 flex items-center justify-between">
        <h2 className="text-lg font-bold text-base-content">
          {formData.id ? t("quote.editQuote", "Modifier le devis") : t("quote.newQuote", "Nouveau devis")}
        </h2>
        <div className="flex flex-col items-end gap-1">
          <span className="badge badge-outline text-base-content/70 font-semibold">
            {formData.status || "pending"}
          </span>
          {formData.status === "validated" && formData.validated_at && (
            <span className="text-[11px] text-emerald-600 whitespace-nowrap">
              ✓ {t("quote.validatedAt", "Validé le")} {fmtDateTime(formData.validated_at)}
            </span>
          )}
        </div>
      </div>

      {/* Bannière commentaires — visible dès l'ouverture, discrète si vide */}
      {(logistics?.comments || "").trim() && (
        <div className="mx-6 mt-4 flex items-start gap-2.5 rounded-xl border border-amber-300/60 bg-amber-500/10 px-4 py-3 text-amber-600">
          <FaCommentDots className="mt-0.5 shrink-0 text-amber-500" />
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-amber-600">
              {t("quote.commentsAlert", "Commentaire important")}
            </p>
            <p className="text-sm whitespace-pre-line">{logistics.comments}</p>
          </div>
        </div>
      )}

      {/* Encart récapitulatif — informations essentielles visibles dès l'ouverture */}
      <div className="mx-6 mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <RecapItem
          icon={<FaCalendarAlt />}
          label={t("quote.recapDate", "Date prévue")}
          value={formData.date ? new Date(formData.date).toLocaleDateString("fr-FR") : "—"}
        />
        <RecapItem
          icon={<FaMapMarkerAlt />}
          label={t("quote.recapDestination", "Destination")}
          value={
            logistics?.delivery_city || logistics?.delivery_zip
              ? [logistics.delivery_city, logistics.delivery_zip].filter(Boolean).join(" ")
              : (formData.client_city || "—")
          }
        />
        <RecapItem
          icon={<FaBoxOpen />}
          label={t("quote.recapVolume", "Volume")}
          value={`${safeNumber(logistics?.volume).toFixed(2)} m³`}
        />
        <RecapItem
          icon={<FaUserTie />}
          label={t("quote.recapCommercial", "Commercial")}
          value={formData.commercial_name || user?.username || "—"}
        />
      </div>

      {/* Onglets */}
      <div className="flex border-b border-base-200 px-6 bg-base-100">
        {[
          { id: "info", label: t("quote.infoAndCalc", "Infos & calculs") },
          { id: "logistics", label: t("quote.logistics", "Logistique") },
          { id: "inventory", label: t("quote.inventory", "Inventaire") },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === tab.id
                ? "border-primary text-primary"
                : "border-transparent text-base-content/50 hover:text-base-content"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenu onglets */}
      <div className="px-6 py-5 bg-base-100">
        {activeTab === "info" && (
          <div className="space-y-5">
            <QuoteClientInfo formData={formData} handleChange={handleChange} />
            <QuoteServices
              prestations={prestations}
              setPrestations={setPrestations}
              serviceLabel={serviceLabel}
              setServiceLabel={setServiceLabel}
              servicePrice={servicePrice}
              setServicePrice={setServicePrice}
              distanceKm={distanceKm}
              setDistanceKm={setDistanceKm}
              wearCoef={wearCoef}
              setWearCoef={setWearCoef}
              fuelCost={fuelCost}
              setFuelCost={setFuelCost}
              salaries={salaries}
              setSalaries={setSalaries}
              otherCharges={otherCharges}
              setOtherCharges={setOtherCharges}
              commercialMargin={commercialMargin}
              setCommercialMargin={setCommercialMargin}
              serviceType={formData?.prestation_type}
              providerType={providerType}
              setProviderType={setProviderType}
              subcontractorId={subcontractorId}
              setSubcontractorId={setSubcontractorId}
              subcontractAmount={subcontractAmount}
              setSubcontractAmount={setSubcontractAmount}
              subcontractors={subcontractors}
              discountPercent={discountPercent}
              setDiscountPercent={setDiscountPercent}
            />
          </div>
        )}

        {activeTab === "logistics" && (
          <QuoteLogisticsTab
            logistics={logistics}
            setLogistics={setLogistics}
            totalInventoryVolume={totalInventoryVolume}
            volumeTouched={volumeTouched}
            setVolumeTouched={setVolumeTouched}
          />
        )}

        {activeTab === "inventory" && (
          <InventorySelector
            selectedItems={selectedInventory}
            setSelectedItems={setSelectedInventory}
          />
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-base-200 bg-base-100 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 justify-between">
        <div className="text-sm text-base-content/70">
          HT : <span className="font-bold text-base-content">{totalHT.toFixed(2)} €</span>
          <span className="mx-2 text-base-content/30">|</span>
          TTC : <span className="font-bold text-base-content">{totalTTC.toFixed(2)} €</span>
          <span className="mx-2 text-base-content/30">|</span>
          <span className="text-base-content/50">{totalInventoryVolume.toFixed(2)} m³</span>
        </div>
        <button type="submit" className="btn btn-primary">
          {t("quote.saveQuote", "Enregistrer le devis")}
        </button>
      </div>
    </form>
  );
}

function RecapItem({ icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-base-200 bg-base-200/40 px-3 py-2.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-base-content/40">{label}</p>
        <p className="truncate text-sm font-bold text-base-content">{value}</p>
      </div>
    </div>
  );
}
